import React from 'react';
import { X, GitBranch } from 'lucide-react';

/**
 * Popup de confirmación al mover un activo a FASE = OFERTA o PROCESO COMPRA.
 * Pregunta si el activo debe seguir apareciendo en la card Mediación del
 * Flujo de Adquisición (activa el flag independiente `mediacion_activa`).
 *
 * Props:
 *  - open: boolean.
 *  - targetFaseLabel: etiqueta de la fase destino (ej. "Oferta").
 *  - onAnswer(active: boolean): confirma y persiste (SÍ = true / NO = false).
 *  - onClose(): cancela el cambio de fase.
 */
const ConfirmMediacionActivaModal = ({ open, targetFaseLabel, onAnswer, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="mediacion-activa-modal-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-md w-full"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="mediacion-activa-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-500 text-white">
              <GitBranch className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                Pasar a {targetFaseLabel}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                ¿Mantener en mediación simultáneamente?
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/20 rounded"
            aria-label="Cancelar"
            data-testid="mediacion-activa-modal-close"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="p-5 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Si eliges <strong>Sí</strong>, el activo aparecerá contabilizado en la card
          Mediación del Flujo de Adquisición además de en su nueva fase.
          Podrás cambiar esta decisión más tarde desde la ficha de edición
          (checkbox <em>En mediación activa</em>).
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', background: 'transparent' }}
            data-testid="mediacion-activa-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => onAnswer(false)}
            className="px-4 py-2 text-sm font-semibold rounded border border-slate-400 text-slate-700 bg-white hover:bg-slate-100"
            data-testid="mediacion-activa-no"
          >
            No
          </button>
          <button
            onClick={() => onAnswer(true)}
            className="px-4 py-2 text-sm font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="mediacion-activa-si"
          >
            Sí, mantener
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmMediacionActivaModal;
